import {
  APP_INITIALIZER,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import localeFr from '@angular/common/locales/fr';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TitleStrategy } from '@angular/router';
import { PageTitleStrategy } from '~shared/services/accessibility/page-title.strategy';
import { ThemeService } from '~shared/services/accessibility/theme.service';
import { LocaleService } from '~shared/services/locale.service';
import { AuthenticationService } from '~shared/services/authentication.service';
import { AuthTabSyncService } from '~shared/services/auth/tab-sync/auth-tab-sync.service';
import { CurrentUserService } from '~shared/services/current-user/current-user.service';
import { ModulesService } from '~shared/services/modules/modules.service';
import { registerLocaleData } from '@angular/common';
import { DEFAULT_LANG } from '~constants';
import { AppStoreModule } from './app-store.module';
import { initApp } from './app-initializer';
import {
  HttpBackend,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { AuthenticationInterceptor } from '~shared/interceptor/authentication.interceptor';
import { ApiTranslateLoader } from '~shared/i18n/api-translate.loader';
import { ToastrModule } from 'ngx-toastr';
import { MailPopinModule } from '~shared/components/mail-popin/mail-popin.module';
import { UnsavedChangesModalComponent } from '~shared/components/unsaved-changes-modal/unsaved-changes-modal.component';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { TooltipDelegateService } from '~shared/tooltip/tooltip-delegate.service';

registerLocaleData(localeFr, DEFAULT_LANG);

export function createTranslateLoader(backend: HttpBackend) {
  // HttpBackend (bypass des intercepteurs) : getTranslation est déclenché
  // pendant la construction de TranslateService ; tout passage par les
  // intercepteurs (AuthenticationInterceptor → AuthenticationService →
  // CurrentUserService → BaseApiService → TranslateService) crée un cycle
  // NG0200 et une app blanche au bootstrap.
  return new ApiTranslateLoader(backend);
}
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    AppStoreModule,
    TranslateModule.forRoot({
      defaultLanguage: DEFAULT_LANG,
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      extendedTimeOut: 5000,
      progressBar: true,
      maxOpened: 5,
    }),
    MailPopinModule,
    UnsavedChangesModalComponent,
    MatNativeDateModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButtonModule,
  ],
  providers: [
    // RGAA 8.6 et 13.x : chaque ecran porte un titre distinct, le focus se
    // deplace au contenu et le changement est annonce. Voir PageTitleStrategy.
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [
        LocaleService,
        AuthenticationService,
        CurrentUserService,
        ModulesService,
        AuthTabSyncService,
        TranslateService,
        ThemeService,
      ],
      multi: true,
    },
    // Infobulles instantanées sur toute l'application (icônes, `title`,
    // dates avec heure) : écoute globale posée une fois au démarrage.
    provideAppInitializer(() => inject(TooltipDelegateService).start()),
    {
      provide: LOCALE_ID,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) =>
        localeService.getLanguage() || DEFAULT_LANG,
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) => localeService.getCurrency(),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
